import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Volume2, Star, Flame, Trophy } from 'lucide-react';
import { makeOptions, sayQuestion, randomPraise, randomEncourage, OPTION_COLORS, TABLE_THEMES } from '../lib/tabuada';
import { speak, stopSpeaking } from '../lib/speech';
import { playTap, playCorrect, playWrong, unlockAudio } from '../lib/sound';
import { Confetti } from '../components/game/Confetti';
import { Mascot } from '../components/game/Mascot';

const MODE_META = {
  sequential: { title: 'Vamos responder!', emoji: '🧠' },
  random: { title: 'Teste a memória!', emoji: '🎲' },
  review: { title: 'Revisão amiga', emoji: '💪' },
};

const TAP_SCALE = { scale: 0.94 };

function DotArray({ a, b }) {
  return (
    <div className="mx-auto mb-2 flex flex-wrap justify-center gap-1" style={{ maxWidth: 220 }}>
      {Array.from({ length: a }).map((_, r) => (
        <div key={`row-${r}`} className="flex gap-1">
          {Array.from({ length: b }).map((_, c) => (
            <span key={`dot-${r}-${c}`} className="inline-block h-2.5 w-2.5 rounded-full bg-purple-400" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function QuizScreen({ mode, table, name, voiceId, volume, questions, onComplete, onExit }) {
  const meta = MODE_META[mode];
  const th = TABLE_THEMES[table] || TABLE_THEMES[1];
  const total = questions.length;

  const [idx, setIdx] = useState(0);
  const [wrongPicks, setWrongPicks] = useState([]);
  const [reveal, setReveal] = useState(false);
  const [solved, setSolved] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const [mascotMood, setMascotMood] = useState('idle');

  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const bestStreakRef = useRef(0);
  const wrongQRef = useRef(new Map());
  const advanceRef = useRef(null);

  const q = questions[idx];
  const options = useMemo(() => makeOptions(q), [q]);

  const askQuestion = () => {
    stopSpeaking();
    speak(sayQuestion(q.a, q.b), { voiceId, volume });
  };

  useEffect(() => {
    setWrongPicks([]);
    setReveal(false);
    setSolved(false);
    setMascotMood('talk');
    const t = setTimeout(() => setMascotMood('idle'), 900);
    askQuestion();
    return () => {
      clearTimeout(t);
      clearTimeout(advanceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  useEffect(() => () => stopSpeaking(), []);

  // refs kept in sync so finish() reads latest counts
  const correctCountRef = useRef(0);
  const wrongCountRef = useRef(0);
  useEffect(() => {
    correctCountRef.current = correctCount;
  }, [correctCount]);
  useEffect(() => {
    wrongCountRef.current = wrongCount;
  }, [wrongCount]);

  const finish = () => {
    const accuracy = total ? correctCountRef.current / total : 0;
    const stars = accuracy >= 0.9 ? 3 : accuracy >= 0.6 ? 2 : accuracy > 0 ? 1 : 0;
    onComplete({
      correct: correctCountRef.current,
      wrong: wrongCountRef.current,
      bestStreak: bestStreakRef.current,
      stars,
      total,
      wrongQuestions: [...wrongQRef.current.values()],
      points: correctCountRef.current * 10,
    });
  };

  const advance = () => {
    if (idx + 1 >= total) {
      finish();
    } else {
      setIdx((i) => i + 1);
    }
  };

  const handlePick = (value) => {
    if (solved) return;
    unlockAudio();
    if (value === q.result) {
      const firstTry = wrongPicks.length === 0;
      setSolved(true);
      setConfetti(true);
      setMascotMood('cheer');
      playCorrect();
      speak(randomPraise(name), { voiceId, volume });
      if (firstTry) {
        setCorrectCount((c) => c + 1);
        setStreak((s) => {
          const ns = s + 1;
          bestStreakRef.current = Math.max(bestStreakRef.current, ns);
          return ns;
        });
      }
      setTimeout(() => setConfetti(false), 1800);
      advanceRef.current = setTimeout(advance, 1700);
    } else {
      if (wrongPicks.includes(value)) return;
      playTap();
      playWrong();
      setWrongPicks((w) => [...w, value]);
      setWrongCount((c) => c + 1);
      setReveal(true);
      setStreak(0);
      setMascotMood('sad');
      const key = `${q.a}x${q.b}`;
      if (!wrongQRef.current.has(key)) wrongQRef.current.set(key, { ...q });
      speak(randomEncourage(), { voiceId, volume });
      setTimeout(() => setMascotMood('idle'), 1600);
    }
  };

  const optState = (value) => {
    if (solved && value === q.result) return 'correct';
    if (wrongPicks.includes(value)) return 'wrong';
    if (reveal && value === q.result) return 'hint';
    return 'idle';
  };

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-2xl flex-col px-4 py-5">
      <Confetti show={confetti} />

      {/* top bar */}
      <div className="mb-4 flex items-center justify-between">
        <button
          data-testid="quiz-back-btn"
          onClick={onExit}
          className="btn-3d bg-white px-3 py-2 text-purple-700"
          style={{ borderColor: '#e2e8f0' }}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex gap-2">
          <span className="flex items-center gap-1 rounded-full bg-amber-300 px-3 py-1.5 text-sm font-black text-amber-900 shadow" data-testid="score-stars">
            <Star className="h-4 w-4 fill-amber-600 text-amber-600" /> {correctCount}
          </span>
          <span className="flex items-center gap-1 rounded-full bg-orange-400 px-3 py-1.5 text-sm font-black text-white shadow" data-testid="score-streak">
            <Flame className="h-4 w-4" /> {streak}
          </span>
          <span className="flex items-center gap-1 rounded-full bg-purple-500 px-3 py-1.5 text-sm font-black text-white shadow" data-testid="score-points">
            <Trophy className="h-4 w-4" /> {correctCount * 10}
          </span>
        </div>
      </div>

      <h1 className="mb-1 text-center font-fred text-2xl font-bold text-indigo-900">
        {meta.emoji} {meta.title}
      </h1>
      <p className="mb-3 text-center text-sm font-black text-purple-500" data-testid="quiz-progress">
        Pergunta {Math.min(idx + 1, total)} de {total}
      </p>

      {/* question card */}
      <div className="card-soft relative flex flex-1 flex-col items-center justify-center p-5">
        <div className="absolute -top-10 right-2">
          <Mascot mood={mascotMood} size={96} />
        </div>

        <button
          data-testid="repeat-question-btn"
          onClick={askQuestion}
          className="mb-3 flex items-center gap-2 rounded-full bg-sky-100 px-4 py-2 text-sm font-black text-sky-700"
        >
          <Volume2 className="h-5 w-5" /> Ouça de novo
        </button>

        {mode === 'review' && <DotArray a={q.a} b={q.b} />}

        <motion.div
          key={idx}
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mb-2 font-fred text-6xl font-bold text-slate-800 sm:text-7xl"
          data-testid="quiz-equation"
        >
          {q.a} <span className="text-purple-400">×</span> {q.b} <span className="text-purple-400">=</span>{' '}
          <span className="text-pink-500">?</span>
        </motion.div>

        <AnimatePresence>
          {reveal && !solved && (
            <motion.p
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-2 rounded-full bg-emerald-100 px-4 py-1.5 text-sm font-black text-emerald-700"
              data-testid="reveal-hint"
            >
              😯 A resposta é {q.result}. Toque nela!
            </motion.p>
          )}
        </AnimatePresence>

        {/* options */}
        <div className="mt-3 grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
          {options.map((value, i) => {
            const state = optState(value);
            const base = OPTION_COLORS[i % OPTION_COLORS.length];
            let bg = base.bg,
              border = base.border,
              color = base.text,
              extra = '';
            if (state === 'correct') {
              bg = '#10B981';
              border = '#047857';
              color = '#fff';
              extra = 'anim-glow';
            } else if (state === 'wrong') {
              bg = '#FCA5A5';
              border = '#EF4444';
              color = '#7F1D1D';
            } else if (state === 'hint') {
              bg = '#6EE7B7';
              border = '#059669';
              color = '#053b2c';
              extra = 'anim-glow';
            }
            return (
              <motion.button
                key={`${value}-${i}`}
                data-testid={`answer-option-${i}`}
                whileTap={TAP_SCALE}
                animate={state === 'wrong' ? { x: [0, -8, 8, -6, 6, 0] } : {}}
                onClick={() => handlePick(value)}
                disabled={solved}
                className={`btn-3d h-20 text-4xl sm:h-24 ${extra}`}
                style={{ background: bg, borderColor: border, color }}
              >
                {value}
              </motion.button>
            );
          })}
        </div>

        {/* progress dots */}
        <div className="mt-5 flex justify-center gap-1.5">
          {questions.map((qq, i) => {
            let dotColor = '#e2e8f0';
            if (i < idx) dotColor = th.bg;
            else if (i === idx) dotColor = th.border;
            return (
              <span
                key={`${qq.a}x${qq.b}-${i}`}
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: dotColor }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
